"use client";

import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import type { BookId, OddsDisplay } from "@/lib/odds-screen/live/types";
import type { BookRegulation } from "@/lib/book-catalog";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { ODDS_DISPLAY_LABELS } from "./view-model";
import { BookPanel, SwitchRow, ToggleAllRow, checkState } from "./book-panel";
import { SheetShell } from "./top-menus";
import { Segmented } from "@/components/fantasy-props/chrome";
import { MIN_STAKES, minStakeText, parseMinStake, type MinStake } from "./use-min-stake";

export type SavedBooks = { readonly available: boolean; readonly active: boolean; readonly count: number };

const FORMATS: readonly OddsDisplay[] = ["american", "decimal", "percent"];
const nameOrder = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const SHORT_FORMAT_LABELS: Readonly<Record<OddsDisplay, string>> = { american: "+100", decimal: "Dec.", percent: "%" };
const MIN_STAKE_OPTIONS = MIN_STAKES.map((value) => ({ id: String(value), label: value === 0 ? "Off" : minStakeText(value) }));
const LAYERS = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" /></svg>;
const LAYERS_SMALL = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" /></svg>;

function triggerLabel(saved: SavedBooks, selectedCount: number, hereOn: number, total: number, only: BookId | undefined): string {
  if (saved.available && saved.active) return `My books · ${selectedCount}`;
  if (total > 0 && selectedCount === total && hereOn === total) return `All books · ${selectedCount}`;
  if (selectedCount === 1 && only !== undefined) return sportsbookIdToDisplay(only);
  return `${selectedCount} ${selectedCount === 1 ? "book" : "books"}`;
}

export function BooksMenu({
  books, isBookEnabled, selectedCount, saved, onUseSaved, onShowAll, onNone, onToggle, onOnly, onSetMany, bestOnly = false, onBestOnly, minStake = 0, onMinStake, variant = "labeled", align = "right", compact = false, format, onFormat,
  open: openProp, onOpenChange, groupKeys,
}: {
  books: readonly BookId[];
  isBookEnabled: (bookId: string) => boolean;
  selectedCount: number;
  saved: SavedBooks;
  onUseSaved: () => void;
  onShowAll: () => void;
  onNone: () => void;
  onToggle: (bookId: string) => void;
  onOnly?: (bookId: string) => void;
  onSetMany: (bookIds: readonly string[], enabled: boolean) => void;
  bestOnly?: boolean;
  onBestOnly?: (next: boolean) => void;
  minStake?: MinStake;
  onMinStake?: (next: MinStake) => void;
  variant?: "labeled" | "icon";
  align?: "left" | "right";
  compact?: boolean;
  format?: OddsDisplay;
  onFormat?: (value: OddsDisplay) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  groupKeys?: Readonly<Partial<Record<BookRegulation, string>>>;
}) {
  const { isHere, place } = useBookAvailability();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean | ((value: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (openProp === undefined) setOwnOpen(value);
    onOpenChange?.(value);
  };
  const ordered = useMemo(() => [...books].sort((a, b) => nameOrder.compare(sportsbookIdToDisplay(a), sportsbookIdToDisplay(b))), [books]);
  const selectedLogos = ordered.filter((book) => isBookEnabled(book)).slice(0, 3);
  const here = ordered.filter(isHere);
  const hereOn = here.filter(isBookEnabled).length;
  const label = triggerLabel(saved, selectedCount, hereOn, here.length, selectedLogos[0]);
  const mineIsEverything = place.hereOnly && saved.active && hereOn === here.length;

  const sheet = variant === "icon";
  const allState = checkState(hereOn, here.length);
  const filtered = bestOnly || minStake > 0;
  const filterLabel = `${bestOnly ? ", best price only" : ""}${minStake > 0 ? `, ${minStakeText(minStake)} minimum limit` : ""}`;

  const extras = (
    <>
      {onFormat && format && (
        <div className="mb-1 flex items-center justify-between gap-2 border-b border-surface-line px-1.5 pb-2 pt-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-zinc-500">Odds format</span>
          <div role="group" aria-label="Odds format" className={cn("flex items-center gap-0.5 rounded-md bg-white/[0.04] p-0.5", sheet ? "h-9" : "h-7")}>
            {FORMATS.map((option) => (
              <button key={option} type="button" aria-pressed={format === option} aria-label={ODDS_DISPLAY_LABELS[option]} onClick={() => onFormat(option)} className={cn("data-num rounded font-medium", sheet ? "h-7 min-w-[52px] px-2.5 text-[13px]" : "h-5 px-1.5 text-[11px]", format === option ? "bg-white/[0.10] text-foreground" : "text-zinc-400 hover:text-foreground")}>{SHORT_FORMAT_LABELS[option]}</button>
            ))}
          </div>
        </div>
      )}
      {saved.available && (
        <SwitchRow
          checked={saved.active}
          title="My books"
          detail={mineIsEverything ? "Your books are every book available here." : `${saved.count} ${saved.count === 1 ? "book" : "books"} saved in your account`}
          sheet={sheet}
          disabled={mineIsEverything}
          onToggle={saved.active ? onShowAll : onUseSaved}
        />
      )}
      {onBestOnly && (
        <SwitchRow checked={bestOnly} title="Best price only" detail="Only events where one of these books has the market's best price" sheet={sheet} onToggle={() => onBestOnly(!bestOnly)} />
      )}
      {onMinStake && (
        <div className={cn("flex gap-2 px-1.5", sheet ? "flex-col py-3" : "items-center justify-between py-2")}>
          <span className="flex min-w-0 flex-col">
            <span className="text-[13px] font-semibold text-foreground">Minimum limit</span>
            <span className="text-[11px] text-zinc-500">Prices with no known limit still count</span>
          </span>
          <Segmented label="Minimum limit" value={String(minStake)} options={MIN_STAKE_OPTIONS} onChange={(id) => onMinStake(parseMinStake(id))} tall={sheet} stretch={sheet} className="data-num pointer-coarse:h-12 pointer-coarse:[&>button]:h-11" />
        </div>
      )}
      {here.length > 0 && (
        <ToggleAllRow
          state={allState}
          label="All books"
          detail={selectedCount === 0 ? "Pick a book" : `${hereOn} of ${here.length}`}
          sheet={sheet}
          onToggle={allState === "on" ? onNone : onShowAll}
        />
      )}
    </>
  );

  const panel = (
    <BookPanel kind="multi" books={ordered} isSelected={isBookEnabled} onItem={onToggle} onOnly={onOnly} onSetMany={onSetMany} sheet={sheet} groupKeys={groupKeys}>{extras}</BookPanel>
  );

  if (sheet) {
    return (
      <>
        <button type="button" aria-haspopup="dialog" aria-expanded={open} aria-label={`Books, ${label}${filterLabel}`} onClick={() => setOpen(true)} className="relative flex h-11 w-11 items-center justify-center rounded-lg text-zinc-300 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          {LAYERS}
          {filtered && <span aria-hidden="true" className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary shadow-[0_0_0_2px_var(--color-surface-raised)]" />}
        </button>
        <SheetShell open={open} title="Books" onClose={() => setOpen(false)}>
          {open && panel}
        </SheetShell>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Books, ${label}${filterLabel}`}
          className={cn("tap relative flex h-8 items-center gap-1.5 rounded-md px-1.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", open && "bg-white/[0.06]")}
        >
          {filtered && <span aria-hidden="true" className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary shadow-[0_0_0_2px_var(--color-surface-raised)]" />}
          {compact ? LAYERS_SMALL : selectedLogos.length > 0 ? (
            <span className="flex items-center">
              {selectedLogos.map((book, index) => (
                <span key={book} className="rounded-[4px] shadow-[0_0_0_1.5px_var(--color-surface-raised)]" style={{ marginLeft: index === 0 ? 0 : -5 }}>
                  <SportsbookLogoMark sportsbook={book} size={16} decorative />
                </span>
              ))}
            </span>
          ) : LAYERS_SMALL}
          <span className="data-num whitespace-nowrap">{label}{minStake > 0 && ` · ${minStakeText(minStake)}+`}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-zinc-500"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-label="Books"
        align={align === "left" ? "start" : "end"}
        sideOffset={6}
        className="odds-black flex max-h-[var(--radix-popover-content-available-height)] w-[620px] max-w-[calc(100vw-32px)] flex-col rounded-[10px] border border-surface-line bg-surface-overlay p-0 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)] [backdrop-filter:none] before:hidden motion-reduce:animate-none"
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}
